import * as React from "react";
import { googleRegion } from "../validation/laenderprofil";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";
import { useGooglePlaces } from "../google/useGooglePlaces";
import { PLACEHOLDERS } from "../validation";
import { SperrWert } from "../SperrWert";

/**
 * Straße-Feld mit Google-Places-Autocomplete (Deutschland). Bei Auswahl werden
 * Straße + PLZ + Ort + Ortsteil via `onResolved` gefüllt (das Formular schreibt
 * die vier formFields-Keys). Ohne Key/Library → normales Texteingabefeld.
 * Lose typisiert (any) für die Google-Objekte.
 */

export interface ResolvedAddress {
  street: string;
  postalcode: string;
  city: string;
  cityArea: string;
}

const useLocal = makeStyles({
  wrap: { position: "relative" },
  list: {
    position: "absolute",
    zIndex: 40,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "264px",
    overflowY: "auto",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: REFINED.cardShadow,
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  opt: {
    ...shorthands.padding("9px", "12px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    cursor: "pointer",
  },
  optActive: { backgroundColor: REFINED.aub50 },
});

export function AddressAutocomplete({
  label,
  name,
  value,
  onChange,
  onResolved,
  land,
  required,
  placeholder,
  fieldClassName,
  labelClassName,
  inputClassName,
  rightSlot,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  onResolved: (a: ResolvedAddress) => void;
  /**
   * Land der Adresse (deutscher Ländername). Steuert, in welchem Land Google
   * Vorschläge sucht. Ohne Angabe Deutschland — genau wie vor 08/2026, als die
   * Suche fest darauf stand und für eine niederländische Straße kommentarlos
   * eine leere Liste lieferte.
   */
  land?: string;
  required?: boolean;
  /** Eigener Platzhalter; Default ist der Straßen-Platzhalter der Anlage. */
  placeholder?: string;
  /**
   * Optische Einbettung in fremde Masken: Wrapper-, Label- und Input-Klasse
   * lassen sich überschreiben, damit das Widget im „Refined"-Kundenformular
   * nicht wie ein Fremdkörper zwischen PLZ und Ort steht. Ohne diese Props
   * bleibt alles beim bisherigen `formPrimitives`-Aussehen (alle
   * Bestandsaufrufer). Kombination immer über `mergeClasses` — bei Griffel
   * entscheidet sonst die Ladereihenfolge statt der Aufrufer.
   */
  fieldClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  /** Knopf rechts neben dem Feld (z. B. „In Google Maps öffnen"). */
  rightSlot?: React.ReactNode;
}) {
  const s = useFormPrimitiveStyles();
  const l = useLocal();
  const { places } = useGooglePlaces();
  const [preds, setPreds] = React.useState<any[]>([]);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const tokenRef = React.useRef<any>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const regionCode = googleRegion(land);
  // DACH und direkte Nachbarländer Deutschlands
  const DACH_AND_NEIGHBORS = ["de", "at", "ch", "nl", "be", "lu", "fr", "pl", "cz", "dk"];
  const includedRegions = regionCode === "de" ? DACH_AND_NEIGHBORS : [regionCode];

  const fetchPreds = (q: string) => {
    if (!places || !q.trim()) {
      // DEBUG: hasPlaces=false → Library nicht geladen (siehe [GMaps]-Logs in useGooglePlaces).
      console.log("[GMaps] fetchPreds übersprungen", { hasPlaces: !!places, query: q });
      setPreds([]);
      setOpen(false);
      return;
    }
    console.log("[GMaps] fetchPreds: Vorschläge anfordern für", JSON.stringify(q));
    if (!tokenRef.current) tokenRef.current = new places.AutocompleteSessionToken();
    places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: q,
      includedRegionCodes: includedRegions,
      // Die Beschriftung bleibt deutsch — der Sachbearbeiter liest deutsch,
      // auch wenn die Adresse in Belgien liegt.
      language: "de",
      region: regionCode,
      sessionToken: tokenRef.current,
    })
      .then((res: any) => {
        console.log(`[GMaps] fetchPreds: ${res?.suggestions?.length ?? 0} Vorschläge erhalten`, res);
        setPreds(res?.suggestions ?? []);
        setOpen(true);
        setActive(0);
      })
      .catch((err: any) => {
        // Bisher still verschluckt — hier loggen, um API-/Key-Fehler sichtbar zu machen.
        console.error("[GMaps] fetchAutocompleteSuggestions FEHLGESCHLAGEN:", err);
        setPreds([]);
      });
  };

  const onType = (v: string) => {
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPreds(v), 220);
  };

  const choose = async (sug: any) => {
    setOpen(false);
    setPreds([]);
    const pp = sug?.placePrediction;
    if (!pp) return;
    try {
      const place = pp.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });
      const comps: any[] = place.addressComponents ?? [];
      const get = (type: string) => comps.find((c) => (c.types || []).includes(type));
      const route = get("route")?.longText ?? "";
      const num = get("street_number")?.longText ?? "";
      const plz = get("postal_code")?.longText ?? "";
      const city = get("locality")?.longText ?? get("postal_town")?.longText ?? "";
      const area =
        get("sublocality")?.longText ??
        get("sublocality_level_1")?.longText ??
        get("neighborhood")?.longText ??
        "";
      const street = [route, num].filter(Boolean).join(" ").trim();
      const resolved = {
        street: street || (pp.text?.text ?? ""),
        postalcode: plz,
        city,
        cityArea: area,
      };
      console.log("[GMaps] choose: Adresse aufgelöst", resolved);
      onResolved(resolved);
    } catch (err) {
      // Bisher still verschluckt — Place-Details-Fehler sichtbar machen.
      console.error("[GMaps] choose: Place-Details FEHLGESCHLAGEN:", err);
      /* Auswahl fehlgeschlagen — Feld bleibt wie getippt */
    }
    tokenRef.current = null;
  };

  const feld = (
    <div className={l.wrap}>
      <div className={s.inputWrap}>
        <input
          id={name}
          name={name}
          autoComplete="off"
          className={mergeClasses(s.input, inputClassName)}
          value={value}
          placeholder={placeholder ?? PLACEHOLDERS.street}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => {
            if (preds.length) setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, preds.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter") {
              if (open && preds[active]) {
                e.preventDefault();
                choose(preds[active]);
              }
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
        />
        <SperrWert />
      </div>
      {open && preds.length > 0 && (
        <div className={l.list} role="listbox" onMouseDown={(e) => e.preventDefault()}>
          {preds.map((sug, i) => {
            const text = sug?.placePrediction?.text?.text ?? "";
            return (
              <div
                key={i}
                role="option"
                className={mergeClasses(l.opt, i === active && l.optActive)}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(sug)}
              >
                {text}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className={mergeClasses(s.field, fieldClassName)}>
      <label className={mergeClasses(s.label, labelClassName)} htmlFor={name}>
        {label}
        {required && <span className={s.req}> *</span>}
      </label>
      {rightSlot ? (
        <div className={s.fieldWithSlot}>
          {feld}
          {rightSlot}
        </div>
      ) : (
        feld
      )}
    </div>
  );
}

export default AddressAutocomplete;
